import React, { useState, useMemo, useEffect } from "react";
import { BreadcrumbComponent } from "../../../common/components";
import { Box } from "@mui/material";
import "./create-campaign.scss";
import CardComponent from "../../../common/components/card-component/card-component";
import { Formik, Form } from "formik";
import TextInputComponent from "../../../common/components/text-input-component/text-input-component";
import SelectInputComponent from "../../../common/components/select-input-component/select-input-component";
import DescriptionInputComponent from "../../../common/components/description-input/DescriptionInputComponent";
import RevenueInputComponent from "../../../common/components/revenue-input/RevenueInputComponent";
import { campainCreate } from "../../../api/campaign";
import AlertComponent from "../../../common/components/alert-component/alert-component";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  STATUS_OPTIONS,
  TYPE_OPTIONS,
} from "../../../constants/campaignDropdown";
import leadInfoIcon from "../../../assets/icons/leadInfo.svg";
import { routes } from "../../../constants/routes";
import leadDetailsIcon from "../../../assets/icons/leadDetails.svg";
import descriptionIcon from "../../../assets/icons/description.svg";
import { useSetLoading } from "../../../common/hooks/appearance";
import { initialValues, validationSchema } from "./create-campaign-helper";
import { getUserDetails } from "../../../api/lead";
import SearchableSelectOwner from "../../../common/components/select-owner/select-owner-compoent";
import DatePickerComponent from "../../../common/components/date-picker/date-picker-compoenent";
import { getCurrentUser } from "../../../common/routines/user-operations";
import MODULES from "../../../constants/modules";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";

const breadcrumbLink = [
  { label: "Campaigns", link: routes.CAMPAIGNLIST, },
  { label: "Create Campaign", link: "#", handleClick: () => {} },
];
export interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}

const CreateCampaign: React.FC = () => {
  const { canWrite, canDelete } = useResourcePermissions(
    MODULES.CREATE_CAMPAIGNS
  );
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  // const [selectedType, setSelectedType] = useState("Advertisement");
  const [selectedCampaignStatus, setSelectedCampaignStatus] =
    useState("Planning");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [isNavigate, setIsNavigate] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const handleAlertClose = () => {
    setAlertOpen(false);
  };
  const navigate = useNavigate();
  const handleCancel = async (values: any) => {
    navigate(routes.CAMPAIGNLIST);
  };
  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Create Campaigns");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));

        setLeadOptions(formattedOptions);

        // Set default campaign owner
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setFormInitialValues((prev: any) => ({
            ...prev,
            campaignOwner: matchedUser.value,
          }));
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };

    fetchCreatedByOptions();
  }, []);
  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const campaignDetails = {
        campaignOwnerId: values.campaignOwner,
        campaignName: values.campaignName,
        startDate: values.startdate ? values.startdate : null,
        endDate: values.enddate ? values.enddate : null,
        type: values.type || null,
        status: selectedCampaignStatus || null,
        budgetedCost: values.budgetCost || null,
        actualCost: values.actualCost || null,
        expectedRevenue: values.expectedRevenue || null,
        expectedResponse: values.expectedResponse || null,
        description: values.description || null,
      };

      const response = await campainCreate(campaignDetails, "Create Campaigns"); // 🚀 JSON call instead of FormData

      if (response.data.success) {
        setLoading(false);

        if (isNavigate) {
          navigate(routes.CAMPAIGNLIST, {
            state: {
              message: "Campaign created successfully",
              type: "success",
            },
          });
        } else {
          setAlertMessage("Campaign saved successfully!");
          setAlertType("success");
          setAlertOpen(true);
          resetForm();
          setSelectedCampaignStatus("Planning");
        }
      } else {
        setAlertMessage("Failed to save campaign.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the campaign."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <div className="campaigncreate-wrapper">
          <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
        <div className="campaign-create-filter-wrapper">
          <Formik
            enableReinitialize
            initialValues={formInitialValues}
            validationSchema={validationSchema(false)}
            onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              validateField,
              errors,
              touched,
              setFieldTouched,
            }) => (
              <Form>
                <div className="campaign-form-wrapper">
                  <CardComponent
                    title="Campaign Information"
                    icon={leadDetailsIcon}
                  >
                    <div className="form-grid">
                      <SearchableSelectOwner
                        fieldName="campaignOwner"
                        options={leadOptions}
                        isShowError={!values?.campaignOwner}
                        placeholder="Select Owner"
                        optional={true}
                        value={
                          leadOptions.find(
                            (opt) => opt.value === values?.campaignOwner
                          ) || null
                        }
                        onChange={(selectedOption: OwnerOption | null) => {
                          if (selectedOption) {
                            setFieldValue(
                              "campaignOwner",
                              selectedOption.value
                            ); // saving only the ID
                          } else {
                            setFieldValue("campaignOwner", null); // handle null case
                          }
                        }}
                        handleBlur={() => {
                          setFieldTouched("campaignOwner", true);
                        }}
                        label="Campaign Owner"
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="campaignName"
                        label="Campaign Name"
                        placeholder="Enter Campaign Name"
                        optional={true}
                        disabled={false}
                        value={values.campaignName}
                        onChange={(e: any) =>
                          setFieldValue("campaignName", e?.target?.value)
                        }
                        maxlength="100"
                      />
                      <DatePickerComponent
                        fieldName="startdate"
                        label="Start Date"
                        value={values?.startdate || null}
                        onChange={(date) => setFieldValue("startdate", date)}
                        minDate={dayjs("1900-01-01")}
                        maxDate={values?.enddate || undefined}
                        optional={true}
                      />
                      <DatePickerComponent
                        fieldName="enddate"
                        label="End Date"
                        value={values?.enddate || null}
                        onChange={(date) => setFieldValue("enddate", date)}
                        minDate={
                          values?.startdate
                            ? dayjs(values.startdate).isBefore(
                                dayjs("1900-01-01")
                              )
                              ? dayjs("1900-01-01")
                              : dayjs(values.startdate)
                            : undefined
                        }
                        optional={true}
                      />
                      <SelectInputComponent
                        fieldName="type"
                        label="Type"
                        value={values?.type}
                        onChange={(e: any) => {
                          setFieldValue("type", e);
                        }}
                        options={TYPE_OPTIONS}
                        optional={true}
                        isShowError={!values.type}
                        placeholder="Select Type"
                      />
                      <SelectInputComponent
                        fieldName="status"
                        label="Status"
                        options={STATUS_OPTIONS}
                        onChange={(value: string) => {
                          setFieldValue("status", value);
                          setSelectedCampaignStatus(value);
                        }}
                        value={selectedCampaignStatus}
                      />
                      <RevenueInputComponent
                        fieldName="expectedRevenue"
                        label="Expected Revenue"
                        optional={false}
                        disabled={false}
                        value={values.expectedRevenue}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("expectedRevenue", input);
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder=""
                      />
                      <TextInputComponent
                        type="text"
                        fieldName="expectedResponse"
                        label="Expected Response"
                        placeholder="Enter Expected Response"
                        optional={false}
                        disabled={false}
                        value={values.expectedResponse}
                        onChange={(e: any) =>
                          setFieldValue("expectedResponse", e?.target?.value)
                        }
                        maxlength="50"
                      />
                      <RevenueInputComponent
                        fieldName="budgetCost"
                        label="Budgeted Cost"
                        optional={false}
                        disabled={false}
                        value={values.budgetCost}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("budgetCost", input);
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder=""
                      />
                      <RevenueInputComponent
                        fieldName="actualCost"
                        label="Actual Cost"
                        optional={false}
                        disabled={false}
                        value={values.actualCost}
                        onChange={(e) => {
                          const input = e.target.value;
                          if (/^\d*\.?\d*$/.test(input) && input.length <= 15) {
                            setFieldValue("actualCost", input);
                          }
                        }}
                        logo={leadInfoIcon}
                        placeholder=""
                      />
                    </div>
                  </CardComponent>
                  <div className="card-container">
                    <CardComponent title="Description" icon={descriptionIcon}>
                      <div className="form-grid-three">
                        <DescriptionInputComponent
                          fieldName="description"
                          label="Company Description"
                          placeholder="Add Description"
                          optional={false}
                          disabled={false}
                          value={values.description}
                          onChange={(e: any) =>
                            setFieldValue("description", e?.target?.value)
                          }
                          maxlength={150}
                          rows={9}
                        />
                      </div>
                    </CardComponent>
                  </div>
                </div>

                <AlertComponent
                  message={alertMessage}
                  type={alertType}
                  open={alertOpen}
                  onClose={handleAlertClose}
                />
                <Box className="footer">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    disabled={!canWrite}
                    onClick={() => {
                      handleSubmit();
                      setIsNavigate(false);
                    }}
                  >
                    Save & New
                  </button>
                  <button
                    type="button"
                    className="cancel-button next-button-wrapper"
                    onClick={() => {
                      handleSubmit();
                      setIsNavigate(true);
                    }}
                    disabled={!canWrite}
                  >
                    Save
                  </button>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CreateCampaign;
